import { useState } from 'react'

import { type FmcsaCargo } from '@/components/freight/FmcsaCargoHints'
import { FreightAllocationEditor } from '@/components/freight/FreightAllocationEditor'
import { FreightEditorAudience } from '@/components/freight/freightEditorAudience'
import { Button } from '@/components/ui/button'
import { type SuggestionGroup } from '@/components/ui/tag-input'
import { dropZeroShares, MAX_FREIGHT_TYPES, normalizeShares } from '@/lib/freightAllocation'
import { type ChatActionWithValue } from '@/models/chat'
import { type CommodityItem } from '@/models/user'

// The chat unmounts its input while an answer is pending, so a rejected answer would come back empty without this.
const draftsByOrg = new Map<string, CommodityItem[]>()

interface ChatFreightAllocationProps {
  suggestions: string[]
  suggestionGroups: SuggestionGroup[]
  fmcsaCargo: FmcsaCargo[]
  defaultValue: CommodityItem[]
  orgId?: string
  handleSubmit: (data: ChatActionWithValue) => void
  readonly?: boolean
}

/** The application's commodities step: pick freight types and their shares, then continue. */
export function ChatFreightAllocation({
  suggestions,
  suggestionGroups,
  fmcsaCargo,
  defaultValue,
  orgId = '',
  handleSubmit,
  readonly,
}: ChatFreightAllocationProps) {
  const [value, setValue] = useState<CommodityItem[]>(() =>
    defaultValue.length > 0 ? normalizeShares(defaultValue) : (draftsByOrg.get(orgId) ?? [])
  )

  function onChange(next: CommodityItem[]) {
    setValue(next)
    draftsByOrg.set(orgId, next)
  }
  const kept = dropZeroShares(value)

  function onSubmit() {
    handleSubmit({
      content: {
        component: 'UL',
        content: kept.map((item) => ({ component: 'LI', content: `${item.name} (${item.percentage}%)` })),
      },
      value: kept.map((item) => ({ name: item.name, percentage: item.percentage })),
    })
  }

  return (
    <div className="flex w-full flex-col gap-4 md:w-3/4">
      <FreightAllocationEditor
        id="chat-freight"
        value={value}
        onChange={onChange}
        suggestions={suggestions}
        suggestionGroups={suggestionGroups}
        fmcsaCargo={fmcsaCargo}
        audience={FreightEditorAudience.CUSTOMER}
        disabled={readonly}
      />
      {!readonly && (
        <Button
          className="self-end"
          disabled={kept.length === 0 || kept.length > MAX_FREIGHT_TYPES}
          onClick={onSubmit}
        >
          Continue
        </Button>
      )}
    </div>
  )
}
